import React, { useMemo, useState } from 'react';
import { useCreditTransactions } from '@dopomogai/agent-kit';
import { Card, Spinner, Alert, IconButton, Badge } from '@dopomogai/ui';
import type { CreditTransactionItem } from '@dopomogai/types';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, SearchX } from 'lucide-react';

const ITEMS_PER_PAGE = 20;

const formatCredits = (amount: number) => {
  const formatter = new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 6,
  });

  return formatter
    .format(amount)
    .replace(/(\.\d*?[1-9])0+$/, '$1')
    .replace(/\.$/, '');
};

const formatUsd = (n: number | null | undefined) => {
  const v = Number(n || 0);
  if (!Number.isFinite(v)) return '$0.0000';
  return `$${v.toFixed(4)}`;
};

const formatPct = (n: number | null | undefined) => {
  if (n === null || n === undefined) return '—';
  return `${n.toFixed(1)}%`;
};

const TransactionRow: React.FC<{ tx: CreditTransactionItem; index: number }> = ({ tx, index }) => {
  const isCredit = tx.amount > 0;
  const badgeColor = isCredit
    ? 'green'
    : tx.transaction_type === 'usage_deduction'
    ? 'yellow'
    : 'red';

  const usage = tx.llm_usage;
  const userCost = usage?.total_cost_usd;
  const providerCost = usage?.actual_provider_cost_usd;
  const impactUsd = usage?.impact_usd;
  const impactPct = usage?.impact_pct ?? 33;

  return (
    <motion.div
      className="grid grid-cols-12 gap-3 items-center p-4 border-b border-slate-200 last:border-b-0 text-sm hover:bg-slate-50 transition-colors"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, delay: index * 0.03 }}
    >
      <div className="col-span-3 text-slate-600">
        {new Date(tx.created_at).toLocaleString(undefined, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })}
      </div>

      <div className="col-span-2">
        <Badge
          text={tx.transaction_type.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
          accentColor={badgeColor}
        />
      </div>

      <div className="col-span-3 text-slate-700 truncate" title={tx.description || undefined}>
        {tx.description || '—'}
        {usage?.model_name ? (
          <div className="text-[11px] text-slate-500 truncate">
            {usage.model_name} {usage.provider ? `• ${usage.provider}` : ''}
          </div>
        ) : null}
      </div>

      <div
        className={`col-span-1 text-right font-bold ${
          isCredit ? 'text-green-600' : 'text-red-600'
        }`}
      >
        {isCredit ? '+' : ''}
        {formatCredits(tx.amount)}
      </div>

      <div className="col-span-1 text-right">
        <div className="font-mono text-slate-900">{formatUsd(userCost)}</div>
        <div className="text-[11px] text-slate-400">user</div>
      </div>

      <div className="col-span-1 text-right">
        <div className="font-mono text-slate-900">{formatUsd(providerCost)}</div>
        <div className="text-[11px] text-slate-400">provider</div>
      </div>

      <div className="col-span-1 text-right">
        <div className="font-mono text-slate-900">{formatUsd(impactUsd)}</div>
        <div className="text-[11px] text-slate-400">{formatPct(impactPct)}</div>
      </div>
    </motion.div>
  );
};

export const TransactionHistoryList: React.FC = () => {
  const [offset, setOffset] = useState(0);
  const { transactions, total, isLoading, error } = useCreditTransactions(ITEMS_PER_PAGE, offset);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / ITEMS_PER_PAGE)), [total]);
  const currentPage = useMemo(() => Math.floor(offset / ITEMS_PER_PAGE) + 1, [offset]);

  const handleNext = () => {
    if (currentPage < totalPages) setOffset((prev) => prev + ITEMS_PER_PAGE);
  };

  const handlePrev = () => {
    if (offset > 0) setOffset((prev) => prev - ITEMS_PER_PAGE);
  };

  return (
    <Card
      title="Transaction History"
      noPadding
      actionButton={
        <div className="flex items-center gap-2">
          <IconButton
            ariaLabel="Previous page"
            icon={<ChevronLeft className="h-4 w-4" />}
            variant="outline"
            onClick={handlePrev}
            disabled={offset === 0 || isLoading}
          />
          <IconButton
            ariaLabel="Next page"
            icon={<ChevronRight className="h-4 w-4" />}
            variant="outline"
            onClick={handleNext}
            disabled={currentPage >= totalPages || isLoading}
          />
        </div>
      }
    >
      <div className="font-bold grid grid-cols-12 gap-3 p-4 bg-slate-100 border-b-2 border-slate-200 text-xs text-slate-500 uppercase tracking-wider">
        <div className="col-span-3">Date</div>
        <div className="col-span-2">Type</div>
        <div className="col-span-3">Description</div>
        <div className="col-span-1 text-right">Credits</div>
        <div className="col-span-1 text-right">User $</div>
        <div className="col-span-1 text-right">Provider $</div>
        <div className="col-span-1 text-right">Impact $</div>
      </div>

      <div className="min-h-[300px]">
        {isLoading ? (
          <div className="flex justify-center items-center p-16">
            <Spinner text="Loading transactions..." size="lg" />
          </div>
        ) : error ? (
          <div className="p-4">
            <Alert type="error" title="Failed to load transactions" message={error.message} />
          </div>
        ) : (
          <AnimatePresence>
            {transactions.length === 0 ? (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center text-slate-500 p-12">
                <SearchX className="mx-auto h-12 w-12 text-slate-400 mb-4" />
                No transactions found.
              </motion.div>
            ) : (
              transactions.map((tx, index) => <TransactionRow key={tx.id} tx={tx} index={index} />)
            )}
          </AnimatePresence>
        )}
      </div>
    </Card>
  );
};